import React from 'react';
import { useTranslation } from 'react-i18next';
import { faBell, faCogs, faClipboardList, faBoxOpen, faLock } from '@fortawesome/free-solid-svg-icons';
import {
  SectionTitle, BlockHeadingIcon, ApiTable, CodeBlock, InfoCard, NumberedList, Panel,
} from '../../../components/apidocs';
import {
  WEBHOOK_EVENT_HEADERS, buildWebhookEventRows,
  WEBHOOK_PAYLOAD_SAMPLES, WEBHOOK_VERIFY_SAMPLE,
} from '../data/webhooks';

const WebhooksSection: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div>
      <SectionTitle icon={faBell} iconColor="bg-gradient-to-br from-purple-400 to-pink-500">
        Webhooks
      </SectionTitle>
      <p className="text-gray-500 dark:text-slate-400 mb-5 text-sm">{t('apiDocs.webhookDesc')}</p>

      <Panel>
        <BlockHeadingIcon icon={faCogs} margin="mb-4">{t('apiDocs.webhookSetup')}</BlockHeadingIcon>
        <NumberedList
          variant="muted"
          items={[t('apiDocs.webhookStep1'), t('apiDocs.webhookStep2'), t('apiDocs.webhookStep3'), t('apiDocs.webhookStep4')]}
        />
      </Panel>

      <InfoCard type="warning" title={t('apiDocs.webhookHeaders')}>
        <code className="bg-amber-100 px-1.5 py-0.5 rounded text-xs font-mono">X-Webhook-Signature</code> - {t('apiDocs.webhookSignatureDesc')}<br/>
        <code className="bg-amber-100 px-1.5 py-0.5 rounded text-xs font-mono">Content-Type: application/json</code>
      </InfoCard>

      {/* Webhook Events */}
      <BlockHeadingIcon icon={faClipboardList}>Webhook Events</BlockHeadingIcon>
      <ApiTable headers={WEBHOOK_EVENT_HEADERS} rows={buildWebhookEventRows(t)} />

      <div className="mt-4">
        <InfoCard type="info" title="P2P: ยอดถอนอาจเข้าเป็นหลายงวด — อ่าน 3 ฟิลด์นี้">
          ร้านที่เปิดโหมดจ่ายแบบ P2P จะได้ webhook <code className="bg-sky-100 dark:bg-sky-900/40 px-1 rounded text-xs font-mono">payout.funded</code> <strong>ทุกงวดที่เงินเข้า</strong> และ{' '}
          <code className="bg-sky-100 dark:bg-sky-900/40 px-1 rounded text-xs font-mono">payout.completed</code> เมื่อครบยอด — ทั้งสอง event มี 3 ฟิลด์เพิ่ม:
          <ul className="list-disc ml-5 mt-2 space-y-1">
            <li><code className="text-xs font-mono">paid_amount</code> — ยอดเงินเข้า<strong>งวดนี้</strong></li>
            <li><code className="text-xs font-mono">funded_amount</code> — ยอด<strong>สะสม</strong>ที่เข้าแล้วทั้งหมด</li>
            <li><code className="text-xs font-mono">remaining_amount</code> — ยอดที่<strong>เหลือ</strong>รอเข้า (0 เมื่อ completed)</li>
          </ul>
          <div className="mt-2">
            เช่น payout 10,000: เข้า 3,000 → <code className="text-xs font-mono">funded</code>&nbsp;3,000 / <code className="text-xs font-mono">remaining</code>&nbsp;7,000 → เข้าอีก 3,000 → 6,000/4,000 → เข้า 4,000 สุดท้าย →{' '}
            <code className="text-xs font-mono">payout.completed</code> (10,000/0) — <strong>ให้ยึด <code className="text-xs font-mono">funded_amount</code> จาก webhook ล่าสุดเสมอ อย่าบวกสะสม <code className="text-xs font-mono">paid_amount</code> เอง</strong>{' '}
            (webhook อาจส่งซ้ำ/สลับลำดับได้) · payout ปกติ (ไม่ใช่ P2P) จะ<strong>ไม่มี</strong> 3 ฟิลด์นี้และไม่มี event <code className="text-xs font-mono">payout.funded</code>
          </div>
        </InfoCard>
      </div>

      <div className="mt-4">
        <InfoCard type="info" title="ฟิลด์ใหม่: ตรวจจับสลิปยอดเกิน (Overpay)">
          เมื่อลูกค้าโอน<strong>เกิน</strong>ยอดที่สั่ง order จะถูกปรับตามยอดที่โอนจริง และ callback <code className="bg-sky-100 dark:bg-sky-900/40 px-1 rounded text-xs font-mono">order.paid</code> จะมี 3 ฟิลด์นี้เสมอ:
          <ul className="list-disc ml-5 mt-2 space-y-1">
            <li><code className="text-xs font-mono">amount_mismatch</code> — <code className="text-xs font-mono">true</code> เมื่อยอดสลิปไม่ตรง (โอนเกิน), ปกติเป็น <code className="text-xs font-mono">false</code></li>
            <li><code className="text-xs font-mono">requested_amount</code> — ยอดเดิมที่สั่งให้โอน (ก่อนปรับ); เป็น <code className="text-xs font-mono">null</code> เมื่อยอดตรงพอดี</li>
            <li><code className="text-xs font-mono">slip_amount</code> — ยอดจริงที่อ่านได้จากสลิป</li>
          </ul>
          <div className="mt-2">กรณีโอนเกิน <code className="text-xs font-mono">base_amount</code>/<code className="text-xs font-mono">total_amount</code> จะถูกปรับเป็นยอดที่โอนจริง เช่น สั่ง 100.45 แต่โอนจริง 150 → <code className="text-xs font-mono">total_amount: 150</code>, <code className="text-xs font-mono">requested_amount: 100.45</code>, <code className="text-xs font-mono">slip_amount: 150</code>, <code className="text-xs font-mono">amount_mismatch: true</code> — ค่าธรรมเนียม/ยอดสุทธิ/ค่าคอมคิดจากยอดที่โอนจริง</div>
        </InfoCard>
      </div>

      {/* Webhook payloads */}
      {WEBHOOK_PAYLOAD_SAMPLES.map((wh, idx) => (
        <div key={idx} className="mb-5">
          <BlockHeadingIcon icon={faBoxOpen} iconClassName={wh.color + ' text-xs'}>{wh.label}</BlockHeadingIcon>
          <CodeBlock code={wh.payload} label={`${wh.event}.json`} id={`wh-${wh.event}`} />
        </div>
      ))}

      {/* Signature Verification */}
      <BlockHeadingIcon icon={faLock}>{t('apiDocs.signatureVerification')}</BlockHeadingIcon>
      <p className="text-gray-500 dark:text-slate-400 text-sm mb-3">{t('apiDocs.signatureDesc')}</p>
      <CodeBlock label="node.js" id="webhook-verify" code={WEBHOOK_VERIFY_SAMPLE} />

      <div className="mt-5">
        <InfoCard type="info" title="Best Practices">
          <ul className="space-y-1 list-disc list-inside">
            <li>{t('apiDocs.bestPractice1')}</li>
            <li>{t('apiDocs.bestPractice2')}</li>
            <li>{t('apiDocs.bestPractice3')}</li>
            <li>{t('apiDocs.bestPractice4')}</li>
          </ul>
        </InfoCard>
      </div>
    </div>
  );
};

export default WebhooksSection;
